/**
 * Deterministic demo data that mirrors the structure of the real
 * app.whitehatbots.com API responses. Seed values tuned so the demo
 * always looks polished when screenshotted.
 */

import { isTradingDay } from "@/lib/trading-calendar";

export interface BrokerAccount {
  id: string;
  /** Where this record came from. Defaults to "mock" for the built-in
   *  demo dataset; set to "real" by the /broker-accounts adapter so the
   *  UI can treat unknown fields (balances, curves, metrics) as pending
   *  instead of misrepresenting the zeros it filled in. */
  source?: "mock" | "real";
  /** How many bots are attached to this account per the WHB API. Only
   *  populated for source === "real"; mock accounts infer the count from
   *  BOTS[]. Displayed as a small badge next to the selector entry. */
  botsCount?: number;
  name: string;
  provider: string;
  masked: string;
  paper: boolean;
  portfolioValue: number;
  /** Total buying power — all cash in the account, *including* cash held by bots. */
  buyingPower: number;
  /** Buying power minus cash currently allocated to bots (manual-trade headroom). */
  buyingPowerExBots: number;
  stocksValue: number;
  cashValue: number;
  stocksCount: number;
  stocksDayChangePct: number;
  allTimeChange: number;
  allTimeChangePct: number;
  /**
   * How the chart should interpret `equityCurve` + `cashflows`.
   *
   *   • `"market-only"` (default) — `equityCurve` is a pure market
   *     walk from the initial deposit; later cashflows are NOT
   *     baked in and get layered on top by the chart's
   *     `fullFromInception` formula (see CHART-SPEC §Portfolio $).
   *     This is the mock-dataset contract.
   *
   *   • `"total-equity"`  — `equityCurve[i]` is already the full
   *     account value on day `i` (cash + stocks + all deposits to
   *     date). The chart must NOT re-layer cashflows on top or it
   *     would double-count. Used by real accounts, whose
   *     `states[].cash + .stocks` snapshot already reflects every
   *     prior deposit, and whose pre-state backfill is cumulative
   *     net deposits (same total-equity interpretation — just
   *     without any market P&L since we don't have per-ticker
   *     historical prices to mark open positions to market on past
   *     days).
   *
   * Defaults to `"market-only"` when undefined to preserve the mock
   * dataset's historical behaviour.
   */
  portfolioBasis?: "market-only" | "total-equity";
  /** SPY price-only return over the same window as equityCurve (% points, rebased to 0). */
  spyReturnPct: number;
  /**
   * TWR-style series rebased to 0% at period start — what the
   * "Portfolio vs SPY — Risk-Adjusted Comparison" chart plots on the portfolio line.
   */
  twrCurve: number[];
  /** SPY price-only cumulative % series rebased to 0 at period start. */
  spyCurve: number[];
  /** SPY-with-dividends-reinvested cumulative % series rebased to 0 at period start. */
  spyTotalCurve: number[];
  /** Ordered dates matching twrCurve/spyCurve/spyTotalCurve (ISO yyyy-mm-dd). */
  curveDates: string[];
  /** Risk / risk-adjusted metrics pre-computed from the curves (TWR-based). */
  risk: RiskMetrics;
  /**
   * User-configured target APY in percent (e.g. `20` = 20 % per year).
   * Rendered as a flat reference line in % mode and as a compounding
   * reference in $ mode.
   */
  targetApyPct: number;
  /**
   * Deposit / withdrawal events plotted on the chart as coloured dots
   * (cyan = deposit, red = withdrawal).
   */
  cashflows: Cashflow[];
  equityCurve: number[];       // ~30 daily points
  botIds: string[];
}

export interface Cashflow {
  /** ISO yyyy-mm-dd — matches `BrokerAccount.curveDates` for plotting. */
  date: string;
  /** Signed dollar amount (positive = deposit, negative = withdrawal). */
  amount: number;
  type: "deposit" | "withdrawal";
}

export interface RiskMetrics {
  /** Weighted Period Return (%, chain-linked TWR) — portfolio side. */
  wprPct: number;
  /** SPY weighted period return (%). */
  wprSpyPct: number;
  /** SPY+Dividends weighted period return (%). */
  wprSpyTotalPct: number;
  /** Annualized return of the portfolio (%, TWR → APY). */
  apyPct: number;
  /** SPY annualized return (%). */
  apySpyPct: number;
  /** SPY+D annualized return (%). */
  apySpyTotalPct: number;
  /** Annualized volatility — stddev of weekly returns × √52 (%). */
  volPct: number;
  volSpyPct: number;
  volSpyTotalPct: number;
  /** Max drawdown (%, always ≤ 0). */
  maxDDPct: number;
  maxDDSpyPct: number;
  maxDDSpyTotalPct: number;
  /** Beta vs SPY price series. */
  beta: number;
  /** Jensen's Alpha (annualized, %). */
  alphaPct: number;
  /** Sharpe ratio (APY − riskFree) / Volatility — portfolio. */
  sharpe: number;
  sharpeSpy: number;
  sharpeSpyTotal: number;
  /** Calmar ratio APY / |MaxDD|. */
  calmar: number;
  calmarSpy: number;
  calmarSpyTotal: number;
}

export interface Bot {
  id: string;
  name: string;
  accountId: string;
  status: "ACTIVE" | "PAUSED";
  allocatedAmount: number;
  currentValue: number;
  performancePct: number;
  lastTradeDate: string;
  sparkline: number[];
  tickers: string[];
  description: string;
  buyingPower: number;
  /** Bot's idle cash slice — earmarked portion of the parent account's
   *  cash balance. Optional because demo seeds don't carry it; real
   *  bots get it via the `/bots/{id}.portfolio.cash` overlay. Used by
   *  `Cash excl bots` in BalancesView (Σ bot.cash subtracted from
   *  account cash to expose the user's controllable cash slice). */
  cash?: number;
  allTimeGain: number;
  allTimeGainPct: number;
  createdAt: string;
  // --- Report-style metrics (matches WHB Weekly Report columns) ---
  /** Time-weighted return, decimal (e.g. 0.113 = 11.3%). Insensitive
   *  to deposits/withdrawals — the industry-standard comparable return. */
  twrPct: number;
  /** Annualised percentage yield, decimal. Derived from TWR + age but
   *  stored so the report doesn't have to recompute on every render. */
  apyPct: number;
  /** Annualised Sharpe ratio (risk-free = 0 for the mock). */
  sharpe: number;
  /** Max drawdown — peak-to-trough decline, negative decimal (e.g. −0.118). */
  maxDDPct: number;
  /** Net deposits: sum of deposits minus withdrawals since inception.
   *  Used as the denominator for dollar-weighted return / contribution. */
  netDeposits: number;
  /** Today's change as a decimal (e.g. 0.006 = +0.60%). Separate from
   *  all-time `performancePct` so the advanced table can surface "DAY%"
   *  without recomputing from the sparkline. */
  dayChangePct: number;
  /** Set when the bot has been deleted from the platform (no longer
   *  in `/bots`) but we still have its last-known data in the local
   *  IndexedDB cache. The Bots tab surfaces these in a separate
   *  "Archived" section below the active list. Sergey 2026-04-30. */
  archived?: boolean;
  /** ISO yyyy-mm-dd of the day we last successfully fetched the bot
   *  from `/bots/{id}`. For an active bot this is "today" (or close
   *  to it); for an archived bot this is the day before deletion —
   *  the snapshot we render in the Bots row + drawer. */
  archivedAt?: string;
  /** Draft bots — unfinished bots the user is still building (no
   *  capital deployed, not trading). Excluded from the All / Active /
   *  Paused filters by default; surfaced under the dedicated "Drafts"
   *  filter, or in "All" when the user opts in — a WHB workflow
   *  addition (Sergey 2026-05-13) so a bot can be saved mid-build and
   *  resumed later. */
  isDraft?: boolean;
  /**
   * Bot type — drives the Structure tab and the Edit-action semantics
   * (Sergey 2026-05-13):
   *   • "rebalancing" — IF/ELSE rebalancing bot. "Edit Structure" →
   *                     visual IF/ELSE editor.
   *   • "wizard"      — wizard-built, non-rebalancing. "Edit Structure"
   *                     → re-run the wizard; "Edit Wizard Bot" → edit
   *                     general params only (bid size, Martingale…).
   *   • "smart-once"  — wizard-built single trade (one trade / entry-
   *                     only / exit-only). Same wizard as "wizard".
   * Optional: undefined is treated as "rebalancing" (the default) so
   * the real-bot adapter doesn't have to populate it yet.
   */
  botType?: "rebalancing" | "wizard" | "smart-once";
}

export interface MarketplaceTemplate {
  id: string;
  name: string;
  creator: string;
  description: string;
  assets: string;
  ar: number;             // annualized return %
  winRate: number;        // %
  maxDrawdown: number;    // max drawdown, stored as a positive % magnitude
  listedDate: string;     // ISO yyyy-mm-dd — drives the "Newest" sort
  tags: string[];
  /**
   * Bot type — drives the Buy flow. A "wizard" template gets a bot
   * settings step (allocated amount, trading time, risk params) before
   * broker assignment; "rebalancing" templates skip straight to broker
   * assignment. Optional — undefined is treated as "rebalancing".
   */
  botType?: "rebalancing" | "wizard";
  // --- Marketplace sort/filter metrics (Sergey 2026-05-13) ---
  /** Cumulative (total, since-inception) return %. */
  cumulativeReturn: number;
  /** Annualized Sharpe ratio. */
  sharpe: number;
  /** Calmar ratio — annualized return ÷ max drawdown. */
  calmar: number;
  /** Annualized standard deviation %. */
  stdDev: number;
  /** Trailing 1-month return %. */
  trailing1M: number;
  /** Trailing 3-month return %. */
  trailing3M: number;
  /** Most recent out-of-sample start date (ISO) — "Recent OOS Date" sort. */
  oosDate: string;
  /**
   * Recovery time — how long the strategy took to climb back to a new
   * high after its worst drawdown, in months. Drives the "Recovery
   * Time" filter (≤ 6m / ≤ 1y). Lower is better.
   */
  recoveryTimeMonths: number;
}

export interface TestingSession {
  id: string;
  startedAt: string;
  botName: string;
  portfolio: string;
  notes: string;
  return: number;
  winRate: number;
  trades: number;
}

export interface StocksList {
  id: string;
  name: string;
  tickers: string[];
  creator: "You" | "WHB";
  updatedAt: string;
}

// ---- seed -----------------------------------------------------------------

function walk(start: number, days: number, vol: number, drift: number, seed: number): number[] {
  let v = start, s = seed;
  const out: number[] = [];
  for (let i = 0; i < days; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const n = (s / 0x7fffffff - 0.5) * 2;
    v = v * (1 + drift + n * vol);
    out.push(+v.toFixed(2));
  }
  return out;
}

/**
 * Daily SPY history point consumed by {@link buildRiskDataForTWR} when
 * the caller wants to skip the synthetic walk and compare against real
 * data. Shape matches the JSON returned by `/api/market/spy`.
 */
export interface SpyPricePoint {
  date: string;       // ISO yyyy-mm-dd
  close: number;      // unadjusted closing price
  adjClose: number;   // split+dividend adjusted (SPY total-return proxy)
}

/**
 * Options for {@link buildRiskDataForTWR}. All fields are optional;
 * leaving them blank reproduces the legacy (synthetic-SPY, synthetic-
 * dates) behaviour used by the mock/demo dataset.
 *
 *   • `curveDates` — one ISO date per TWR point. When supplied, the
 *     synthetic "ending today" dates are replaced with the real ones
 *     so the chart's x-axis renders the account's actual calendar
 *     history. Required when `spyPrices` is supplied so alignment can
 *     work.
 *   • `spyPrices`  — daily SPY history. Forward-filled onto
 *     `curveDates` so weekends/holidays inherit the last trading-day
 *     close; dates before the first available SPY sample keep that
 *     first close as a flat anchor. When supplied this replaces the
 *     synthetic walk and `spyCurve` / `spyTotalCurve` become real.
 */
export interface BuildRiskDataOptions {
  curveDates?: readonly string[];
  spyPrices?: readonly SpyPricePoint[];
}

/**
 * Align a raw daily SPY series to the portfolio's day-level axis.
 *
 * Two pieces of complexity a naive zip() misses:
 *   1. **Market-closed days** — portfolio `states[]` snapshots land
 *      on weekends/holidays, SPY doesn't. Forward-fill by binary-
 *      searching the most recent trading-day close ≤ target date.
 *   2. **Dates before SPY history starts** — if somehow the portfolio
 *      pre-dates 1993 or the SPY fetch returned a short window,
 *      back-fill with the *first* available close so the curve is
 *      flat before real data arrives.
 *
 * Returns `null` when alignment isn't possible (zero points in either
 * input) so the caller can fall back to the synthetic walk.
 */
function alignSpyPrices(
  dates: readonly string[],
  prices: readonly { date: string; close: number; adjClose: number }[],
  pick: (p: { close: number; adjClose: number }) => number
): number[] | null {
  if (dates.length === 0 || prices.length === 0) return null;

  // Build sorted arrays once — we binary-search into them per date.
  const sorted = [...prices].sort((a, b) => a.date.localeCompare(b.date));
  const sortedDates = sorted.map((p) => p.date);

  const firstVal = pick(sorted[0]!);
  let lastVal = firstVal;

  const out: number[] = [];
  for (const target of dates) {
    // Exact hit on a trading day?
    // Binary search for the largest sortedDates[idx] ≤ target.
    let lo = 0;
    let hi = sortedDates.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedDates[mid]! <= target) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) {
      lastVal = pick(sorted[lo - 1]!);
    }
    // lo === 0 means `target` predates all SPY data — keep `firstVal`.
    out.push(lastVal);
  }
  return out;
}

/**
 * Build SPY benchmark curves + full risk-metric bundle for a given
 * **pre-computed TWR curve**.
 *
 * Inputs
 *   • `twrCurve` — cumulative %, rebased to 0 at period start. The
 *     caller is responsible for cashflow-adjustment: for demo data
 *     the simple price-ratio of equityCurve is fine (no cashflows),
 *     but for real accounts we chain-link between cashflow events
 *     (see `computeChainLinkedTWR` in `lib/whb-accounts-adapter`).
 *   • `seed`    — deterministic seed for the SPY synthetic walk. Used
 *     only when real SPY data isn't supplied via `opts.spyPrices`.
 *   • `opts`    — optional real-data injection; see
 *     {@link BuildRiskDataOptions}.
 *
 * Output matches `buildAccountRiskData` verbatim: `{twrCurve,
 * spyCurve, spyTotalCurve, curveDates, spyReturnPct, risk}`.
 *
 * Formulas (TradingView-aligned metrics pipeline):
 *   weekly returns → stddev × √52 = annualized volatility
 *   maxDD on cumulative curves
 *   beta = cov(portRets, spyRets) / var(spyRets)
 *   alpha (annualized, Jensen) = apy − (rf + β × (spyTotalApy − rf))
 *   sharpe = (apy − rf) / volatility
 *   calmar = apy / |maxDD|
 */
export function buildRiskDataForTWR(
  twrCurve: number[],
  seed: number,
  opts: BuildRiskDataOptions = {}
) {
  const days = twrCurve.length;

  // Resolve the x-axis date array. Use caller-supplied real dates when
  // they match the TWR length; otherwise synthesize daily dates
  // ending today (legacy behaviour used by mock-seeded accounts).
  //
  // The synthesis branch walks BACKWARDS over trading days only —
  // weekends + NYSE holidays are skipped — so every twrCurve point
  // lands on a real market session. Matches the reconstruction
  // branch's filter in `whb-accounts-adapter.ts` so the chart's
  // x-axis is consistent across mock + real accounts.
  let curveDates: string[];
  if (opts.curveDates && opts.curveDates.length === days) {
    curveDates = [...opts.curveDates];
  } else {
    const reverseTradingDays: string[] = [];
    const cursor = new Date();
    // Cap the walk at 4× days so a corrupted holiday table or wedge
    // of consecutive closures can't loop forever. In practice ~5/7
    // calendar days are trading days, so 4× is generous.
    const maxIters = days * 4 + 10;
    let iters = 0;
    while (reverseTradingDays.length < days && iters < maxIters) {
      const iso = cursor.toISOString().slice(0, 10);
      if (isTradingDay(iso)) reverseTradingDays.push(iso);
      cursor.setDate(cursor.getDate() - 1);
      iters++;
    }
    // Walked newest → oldest; flip so curveDates is ascending.
    curveDates = reverseTradingDays.reverse();
    // Edge case: if the trading-day walk underfilled (e.g. unrealistic
    // `days` count for the calendar window), pad the front with the
    // earliest date so length still matches twrCurve.length.
    while (curveDates.length < days) {
      curveDates.unshift(curveDates[0] ?? new Date().toISOString().slice(0, 10));
    }
  }

  // Try the real-SPY path first — produces one daily close per
  // portfolio date, forward-filled across weekends. `alignSpyPrices`
  // returns null if the upstream data is absent or misshapen; in
  // that case drop through to the synthetic walk so the chart still
  // has a line to render.
  const realCloseAligned =
    opts.spyPrices && opts.curveDates
      ? alignSpyPrices(curveDates, opts.spyPrices, (p) => p.close)
      : null;
  const realAdjAligned =
    opts.spyPrices && opts.curveDates
      ? alignSpyPrices(curveDates, opts.spyPrices, (p) => p.adjClose)
      : null;

  let spyCurve: number[];
  let spyTotalCurve: number[];
  if (realCloseAligned && realAdjAligned && realCloseAligned[0]! > 0 && realAdjAligned[0]! > 0) {
    // Rebase each aligned series to 0% at window start (portfolio's
    // inception). This is the shape CHART-SPEC mandates — % lines
    // anchor at inception.
    const base = realCloseAligned[0]!;
    spyCurve = realCloseAligned.map((v) => +(((v / base) - 1) * 100).toFixed(3));
    const adjBase = realAdjAligned[0]!;
    spyTotalCurve = realAdjAligned.map((v) => +(((v / adjBase) - 1) * 100).toFixed(3));
  } else {
    // Synthetic fallback — deterministic walk seeded by account id.
    // Still useful for the demo dataset and for real accounts during
    // SPY-fetch failures or initial render before the price feed
    // lands.
    const spyDaily = walk(100, days, 0.010, 0.00040, seed ^ 0xA55A);
    spyCurve = spyDaily.map((v) => +(((v / spyDaily[0]!) - 1) * 100).toFixed(3));
    // SPY + reinvested dividends — a ~1.3 %/yr estimate, prorated
    // linearly. Real adjClose (above) supersedes when available.
    const divYieldAnnual = 0.013;
    spyTotalCurve = spyCurve.map(
      (v, i) => +(v + divYieldAnnual * 100 * (i / 365)).toFixed(3)
    );
  }

  // ── Risk math ─────────────────────────────────────────────────────────
  const weeklyRets = (cum: number[]) => {
    const r: number[] = [];
    // Weekly sample: take every 7th point (the ~52 samples/yr
    // convention used for annualization).
    const step = 7;
    for (let i = step; i < cum.length; i += step) {
      const p = 100 + cum[i - step]!;
      const c = 100 + cum[i]!;
      r.push(((c - p) / p) * 100);
    }
    return r;
  };
  const stdev = (a: number[]) => {
    if (a.length < 2) return 0;
    const m = a.reduce((s, v) => s + v, 0) / a.length;
    return Math.sqrt(a.reduce((s, v) => s + (v - m) ** 2, 0) / (a.length - 1));
  };
  const covar = (a: number[], b: number[]) => {
    const ma = a.reduce((s, v) => s + v, 0) / a.length;
    const mb = b.reduce((s, v) => s + v, 0) / b.length;
    let c = 0;
    for (let i = 0; i < a.length; i++) c += (a[i]! - ma) * (b[i]! - mb);
    return c / (a.length - 1);
  };
  const maxDD = (cum: number[]) => {
    let peak = -Infinity, mdd = 0;
    for (const c of cum) {
      const v = 100 + c;
      if (v > peak) peak = v;
      const dd = ((v - peak) / peak) * 100;
      if (dd < mdd) mdd = dd;
    }
    return mdd;
  };
  const ann = (r: number) => (Math.pow(1 + r / 100, 365 / days) - 1) * 100;

  const riskFree = 4.5;
  const pRets = weeklyRets(twrCurve);
  const sRets = weeklyRets(spyCurve);
  const stRets = weeklyRets(spyTotalCurve);
  const pStd = stdev(pRets), sStd = stdev(sRets), stStd = stdev(stRets);
  const wpy = 52;
  const volPct = pStd * Math.sqrt(wpy);
  const volSpyPct = sStd * Math.sqrt(wpy);
  const volSpyTotalPct = stStd * Math.sqrt(wpy);
  const pDD = maxDD(twrCurve), sDD = maxDD(spyCurve), stDD = maxDD(spyTotalCurve);
  const wprPct = twrCurve[twrCurve.length - 1] ?? 0;
  const wprSpyPct = spyCurve[spyCurve.length - 1] ?? 0;
  const wprSpyTotalPct = spyTotalCurve[spyTotalCurve.length - 1] ?? 0;
  const apyPct = ann(wprPct);
  const apySpyPct = ann(wprSpyPct);
  const apySpyTotalPct = ann(wprSpyTotalPct);
  const beta = sStd > 0 ? covar(pRets, sRets) / (sStd ** 2) : 0;
  const alphaPct = apyPct - (riskFree + beta * (apySpyTotalPct - riskFree));
  const sharpe = volPct > 0 ? (apyPct - riskFree) / volPct : 0;
  const sharpeSpy = volSpyPct > 0 ? (apySpyPct - riskFree) / volSpyPct : 0;
  const sharpeSpyTotal = volSpyTotalPct > 0 ? (apySpyTotalPct - riskFree) / volSpyTotalPct : 0;
  const calmar = Math.abs(pDD) > 0 ? apyPct / Math.abs(pDD) : 0;
  const calmarSpy = Math.abs(sDD) > 0 ? apySpyPct / Math.abs(sDD) : 0;
  const calmarSpyTotal = Math.abs(stDD) > 0 ? apySpyTotalPct / Math.abs(stDD) : 0;

  return {
    twrCurve,
    spyCurve,
    spyTotalCurve,
    curveDates,
    spyReturnPct: wprSpyPct / 100,
    risk: {
      wprPct, wprSpyPct, wprSpyTotalPct,
      apyPct, apySpyPct, apySpyTotalPct,
      volPct, volSpyPct, volSpyTotalPct,
      maxDDPct: pDD, maxDDSpyPct: sDD, maxDDSpyTotalPct: stDD,
      beta, alphaPct,
      sharpe, sharpeSpy, sharpeSpyTotal,
      calmar, calmarSpy, calmarSpyTotal
    } as RiskMetrics
  };
}

/**
 * Legacy path — derives a naive price-ratio TWR from equityCurve
 * (`(v / v[0] − 1) × 100`) and delegates to `buildRiskDataForTWR`.
 *
 * This is mathematically correct only when the period has NO
 * cashflows (demo / mock data). When cashflows exist the result
 * mis-reports returns as inflows/outflows bleed into the curve.
 * Real accounts should use `buildRiskDataForTWR` with a chain-linked
 * TWR instead — see `computeChainLinkedTWR` in the adapter.
 */
export function buildAccountRiskData(equityCurve: number[], seed: number) {
  const start = equityCurve[0] ?? 1;
  const twrCurve = equityCurve.map((v) => +(((v / start) - 1) * 100).toFixed(3));
  return buildRiskDataForTWR(twrCurve, seed);
}

// Per-account equityCurve + seeds used to synthesize SPY trajectories.
//
// 400 days of daily history so the chart's range chips (1M / 3M / 6M /
// YTD / 1Y / ALL) each slice a visibly-different window. The previous
// 30-day seed made every range render the same data, which looked
// broken. Daily drift/vol parameters are scaled to a realistic
// ballpark over the longer window.
const acc1Equity = walk(10000,  400, 0.012, 0.00030, 42);
const acc2Equity = walk(500000, 400, 0.010, 0.00045, 77);
const acc3Equity = walk(200000, 400, 0.009, 0.00040, 113);
// Live accounts — smaller positions, more conservative drift. Seeded
// distinctly (167, 199) so their curves are independent from the
// three paper accounts.
const accLive1Equity = walk(40000,  400, 0.008, 0.00035, 167);
const accLive2Equity = walk(110000, 400, 0.007, 0.00025, 199);
const acc1Risk = buildAccountRiskData(acc1Equity, 42);
const acc2Risk = buildAccountRiskData(acc2Equity, 77);
const acc3Risk = buildAccountRiskData(acc3Equity, 113);
const accLive1Risk = buildAccountRiskData(accLive1Equity, 167);
const accLive2Risk = buildAccountRiskData(accLive2Equity, 199);

/** Helper — build N cashflow rows spread across an account's curveDates. */
function buildCashflows(
  dates: string[],
  rows: Array<{ offset: number; amount: number; type: "deposit" | "withdrawal" }>
): Cashflow[] {
  return rows
    .filter((r) => r.offset >= 0 && r.offset < dates.length)
    .map(({ offset, amount, type }) => ({
      date: dates[offset]!,
      amount: type === "withdrawal" ? -Math.abs(amount) : Math.abs(amount),
      type
    }));
}

// Account-specific cashflow seeds. Placed at days-from-start offsets
// spread across the 400-day window so different range chips show
// different cashflow dots (e.g. 1M window only shows the late
// cashflows, 1Y shows most).
// The offset-0 row is the initial funding deposit that opened the
// account — its amount matches the starting equity of the walk() seed
// above, so sliced.usd[0] and the day-0 cashflow dot agree. Per Sergey:
// "First day in ALL range always starts when first deposit landed" —
// no synthetic opening markers; the first real cashflow IS the opening.
const acc1Cashflows = buildCashflows(acc1Risk.curveDates, [
  { offset: 0,   amount: 10000, type: "deposit"    },
  { offset: 20,  amount: 2000,  type: "deposit"    },
  { offset: 95,  amount: 500,   type: "withdrawal" },
  { offset: 220, amount: 1500,  type: "deposit"    },
  { offset: 340, amount: 750,   type: "deposit"    },
  { offset: 385, amount: 300,   type: "withdrawal" }
]);
const acc2Cashflows = buildCashflows(acc2Risk.curveDates, [
  { offset: 0,   amount: 500000, type: "deposit"    },
  { offset: 30,  amount: 100000, type: "deposit"    },
  { offset: 110, amount: 25000,  type: "withdrawal" },
  { offset: 200, amount: 50000,  type: "deposit"    },
  { offset: 305, amount: 40000,  type: "withdrawal" },
  { offset: 380, amount: 75000,  type: "deposit"    }
]);
const acc3Cashflows = buildCashflows(acc3Risk.curveDates, [
  { offset: 0,   amount: 200000, type: "deposit"    },
  { offset: 45,  amount: 20000,  type: "deposit"    },
  { offset: 180, amount: 10000,  type: "deposit"    },
  { offset: 330, amount: 5000,   type: "withdrawal" }
]);
const accLive1Cashflows = buildCashflows(accLive1Risk.curveDates, [
  { offset: 0,   amount: 40000, type: "deposit"    },
  { offset: 15,  amount: 40000, type: "deposit"    },
  { offset: 140, amount: 5000,  type: "deposit"    },
  { offset: 300, amount: 2500,  type: "withdrawal" }
]);
const accLive2Cashflows = buildCashflows(accLive2Risk.curveDates, [
  { offset: 0,   amount: 110000, type: "deposit"    },
  { offset: 10,  amount: 100000, type: "deposit"    },
  { offset: 95,  amount: 15000,  type: "deposit"    },
  { offset: 275, amount: 7500,   type: "withdrawal" },
  { offset: 360, amount: 5000,   type: "deposit"    }
]);

export const ACCOUNTS: BrokerAccount[] = [
  // Live accounts first — matches the selector's Live-before-Paper ordering
  // so `ACCOUNTS[0]` is a sensible default for new deep-links.
  {
    id: "acc-live-1",
    name: "Alpaca Live",
    provider: "Alpaca",
    masked: "*9027",
    paper: false,
    portfolioValue: 47312.44,
    buyingPower: 18540.12,
    buyingPowerExBots: 8720.45,
    stocksValue: 28772.32,
    cashValue: 18540.12,
    stocksCount: 9,
    stocksDayChangePct: 0.0091,
    allTimeChange: 4812.44,
    allTimeChangePct: 0.113,
    equityCurve: accLive1Equity,
    twrCurve: accLive1Risk.twrCurve,
    spyCurve: accLive1Risk.spyCurve,
    spyTotalCurve: accLive1Risk.spyTotalCurve,
    curveDates: accLive1Risk.curveDates,
    spyReturnPct: accLive1Risk.spyReturnPct,
    risk: accLive1Risk.risk,
    targetApyPct: 15,
    cashflows: accLive1Cashflows,
    botIds: ["bot-live-momentum"]
  },
  {
    id: "acc-live-2",
    name: "IBKR Personal",
    provider: "Interactive Brokers",
    masked: "*4418",
    paper: false,
    portfolioValue: 128430.90,
    buyingPower: 41220.15,
    buyingPowerExBots: 22105.40,
    stocksValue: 87210.75,
    cashValue: 41220.15,
    stocksCount: 12,
    stocksDayChangePct: -0.0032,
    allTimeChange: 15930.90,
    allTimeChangePct: 0.141,
    equityCurve: accLive2Equity,
    twrCurve: accLive2Risk.twrCurve,
    spyCurve: accLive2Risk.spyCurve,
    spyTotalCurve: accLive2Risk.spyTotalCurve,
    curveDates: accLive2Risk.curveDates,
    spyReturnPct: accLive2Risk.spyReturnPct,
    risk: accLive2Risk.risk,
    targetApyPct: 12,
    cashflows: accLive2Cashflows,
    botIds: ["bot-live-swing"]
  },
  {
    id: "acc-1",
    name: "WhiteHatBots",
    provider: "Alpaca Paper",
    masked: "*a1ac",
    paper: true,
    portfolioValue: 9860.69,
    buyingPower: 1000.80,
    buyingPowerExBots: 120.55,
    stocksValue: 8859.89,
    cashValue: 1000.80,
    stocksCount: 22,
    stocksDayChangePct: -0.0615,
    allTimeChange: -139.31,
    allTimeChangePct: -0.0139,
    equityCurve: acc1Equity,
    twrCurve: acc1Risk.twrCurve,
    spyCurve: acc1Risk.spyCurve,
    spyTotalCurve: acc1Risk.spyTotalCurve,
    curveDates: acc1Risk.curveDates,
    spyReturnPct: acc1Risk.spyReturnPct,
    risk: acc1Risk.risk,
    targetApyPct: 20,
    cashflows: acc1Cashflows,
    botIds: ["bot-rsi2-v2"]
  },
  {
    id: "acc-2",
    name: "Alpaca Paper 2025-09-30",
    provider: "Alpaca Paper",
    masked: "*6534",
    paper: true,
    portfolioValue: 525520.92,
    buyingPower: 356503.27,
    buyingPowerExBots: 198420.18,
    stocksValue: 169017.65,
    cashValue: 356503.27,
    stocksCount: 21,
    stocksDayChangePct: -0.1422,
    allTimeChange: 25520.92,
    allTimeChangePct: 0.051,
    equityCurve: acc2Equity,
    twrCurve: acc2Risk.twrCurve,
    spyCurve: acc2Risk.spyCurve,
    spyTotalCurve: acc2Risk.spyTotalCurve,
    curveDates: acc2Risk.curveDates,
    spyReturnPct: acc2Risk.spyReturnPct,
    risk: acc2Risk.risk,
    targetApyPct: 20,
    cashflows: acc2Cashflows,
    botIds: ["bot-paper-v2"]
  },
  {
    id: "acc-3",
    name: "Ellen",
    provider: "Alpaca Paper",
    masked: "*2b31",
    paper: true,
    portfolioValue: 210803.22,
    buyingPower: 85410.18,
    buyingPowerExBots: 42105.09,
    stocksValue: 125393.04,
    cashValue: 85410.18,
    stocksCount: 14,
    stocksDayChangePct: 0.024,
    allTimeChange: 10803.22,
    allTimeChangePct: 0.054,
    equityCurve: acc3Equity,
    twrCurve: acc3Risk.twrCurve,
    spyCurve: acc3Risk.spyCurve,
    spyTotalCurve: acc3Risk.spyTotalCurve,
    curveDates: acc3Risk.curveDates,
    spyReturnPct: acc3Risk.spyReturnPct,
    risk: acc3Risk.risk,
    targetApyPct: 20,
    cashflows: acc3Cashflows,
    botIds: ["bot-ellen-rsi"]
  }
];

export const BOTS: Bot[] = [
  // Live bots first — matches the Live-first ordering we use for
  // accounts and makes `BOTS[0]` a live bot.
  {
    id: "bot-live-momentum",
    name: "Momentum Rotation Live",
    accountId: "acc-live-1",
    status: "ACTIVE",
    allocatedAmount: 40000,
    currentValue: 47312.44,
    performancePct: 0.113,
    lastTradeDate: "2026-04-17",
    sparkline: walk(40000, 60, 0.010, 0.0006, 167),
    tickers: ["SPY", "QQQ", "IEF"],
    description: "Conservative momentum rotation across SPY/QQQ/IEF.",
    buyingPower: 18540.12,
    allTimeGain: 4812.44,
    allTimeGainPct: 0.113,
    createdAt: "2025-08-12",
    // Conservative rotation — TWR ≈ all-time gain (few deposits since
    // inception), annualised over ~248 days → mid-teens APY. Sharpe
    // comfortably above 1 reflects the defensive-asset tilt.
    twrPct: 0.113,
    apyPct: 0.173,
    sharpe: 1.42,
    maxDDPct: -0.042,
    netDeposits: 40000,
    dayChangePct: 0.0042
  },
  {
    id: "bot-live-swing",
    name: "Mean-Reversion Swing",
    accountId: "acc-live-2",
    status: "ACTIVE",
    allocatedAmount: 110000,
    currentValue: 128430.90,
    performancePct: 0.141,
    lastTradeDate: "2026-04-16",
    sparkline: walk(110000, 60, 0.009, 0.0005, 199),
    tickers: ["AAPL", "MSFT", "NVDA", "GOOGL"],
    description: "Mean-reversion swing trader on mega-cap tech.",
    buyingPower: 41220.15,
    allTimeGain: 15930.90,
    allTimeGainPct: 0.141,
    createdAt: "2025-06-30",
    // Mega-cap swing trader — higher returns but beefier drawdowns
    // (mean reversion misfires when mega-caps trend). Sharpe still
    // solidly >1 thanks to a large hit-rate.
    twrPct: 0.141,
    apyPct: 0.178,
    sharpe: 1.26,
    maxDDPct: -0.087,
    netDeposits: 110000,
    dayChangePct: 0.0061
  },
  {
    id: "bot-paper-v2",
    name: "Paper V2",
    accountId: "acc-2",
    status: "ACTIVE",
    allocatedAmount: 500000,
    currentValue: 525520.92,
    performancePct: 0.051,
    lastTradeDate: "2026-04-14",
    sparkline: walk(500000, 60, 0.015, 0.0006, 11),
    tickers: ["SPY", "QQQ", "TLT", "GLD", "IEF"],
    description: "Multi-asset momentum rotator with volatility filter.",
    buyingPower: 356503.27,
    allTimeGain: 25520.92,
    allTimeGainPct: 0.051,
    createdAt: "2025-09-30",
    // Slow-moving multi-asset rotator with a vol filter — modest TWR,
    // smooth ride (low vol → above-average Sharpe despite a small MaxDD).
    twrPct: 0.051,
    apyPct: 0.093,
    sharpe: 1.11,
    maxDDPct: -0.038,
    netDeposits: 500000,
    dayChangePct: 0.0021
  },
  {
    id: "bot-ellen-rsi",
    name: "Ellen Paper RSI 2.0",
    accountId: "acc-3",
    status: "ACTIVE",
    allocatedAmount: 200000,
    currentValue: 210803.22,
    performancePct: 0.054,
    lastTradeDate: "2026-04-16",
    sparkline: walk(200000, 60, 0.013, 0.0005, 23),
    tickers: ["TQQQ", "SQQQ", "UVXY"],
    description: "RSI-2 dip buyer with trailing stop on leveraged ETFs.",
    buyingPower: 85410.18,
    allTimeGain: 10803.22,
    allTimeGainPct: 0.054,
    createdAt: "2025-09-30",
    // Leveraged ETF dip buyer — higher absolute DD, Sharpe below 1
    // as the volatility of TQQQ/SQQQ/UVXY penalises it. Still
    // respectable APY thanks to a few large wins.
    twrPct: 0.054,
    apyPct: 0.099,
    sharpe: 0.82,
    maxDDPct: -0.164,
    netDeposits: 200000,
    dayChangePct: -0.0029
  },
  {
    id: "bot-rsi2-v2",
    name: "Dynamic Long Daily Martingale RSI2 v2",
    accountId: "acc-1",
    status: "ACTIVE",
    botType: "wizard",
    allocatedAmount: 10000,
    currentValue: 9860.69,
    performancePct: -0.0139,
    lastTradeDate: "2026-04-16",
    sparkline: walk(10000, 60, 0.018, -0.0003, 51),
    tickers: ["TQQQ"],
    description: "Martingale RSI-2 with dynamic position sizing on TQQQ.",
    buyingPower: 1000.80,
    allTimeGain: -139.31,
    allTimeGainPct: -0.0139,
    createdAt: "2026-01-01",
    // Young bot, underwater — Sharpe outright negative, drawdown
    // fresher than the total P&L (tops of the equity curve were
    // somewhere in between).
    twrPct: -0.0139,
    apyPct: -0.048,
    sharpe: -0.31,
    maxDDPct: -0.071,
    netDeposits: 10000,
    dayChangePct: -0.0053
  },
  {
    id: "bot-fake-rsi",
    name: "Fake RSI 2.0",
    accountId: "acc-1",
    status: "ACTIVE",
    allocatedAmount: 200000,
    currentValue: 207122.91,
    performancePct: 0.0356,
    lastTradeDate: "2026-04-08",
    sparkline: walk(200000, 60, 0.010, 0.0004, 97),
    tickers: ["SPY", "QQQ"],
    description: "RSI-2 entry with ATR-based exits.",
    buyingPower: 45203.12,
    allTimeGain: 7122.91,
    allTimeGainPct: 0.0356,
    createdAt: "2025-12-18",
    // Rsi-2 with ATR exits — steady mid-single-digit TWR, tight
    // drawdowns, Sharpe hovering around 1.
    twrPct: 0.0356,
    apyPct: 0.107,
    sharpe: 0.98,
    maxDDPct: -0.048,
    netDeposits: 200000,
    dayChangePct: 0.0014
  },
  {
    id: "bot-martingale-2",
    name: "Martingale 2.0 strategy",
    accountId: "acc-1",
    status: "ACTIVE",
    botType: "wizard",
    allocatedAmount: 20000,
    currentValue: 20784.00,
    performancePct: 0.0392,
    lastTradeDate: "2026-04-16",
    sparkline: walk(20000, 60, 0.012, 0.00045, 139),
    tickers: ["SPY"],
    description: "Classic martingale with risk cap.",
    buyingPower: 5233.51,
    allTimeGain: 784.00,
    allTimeGainPct: 0.0392,
    createdAt: "2026-02-01",
    // Very young classic martingale — punchy APY extrapolation
    // because the base is only ~10 weeks, Sharpe above 1 but the
    // sample size is small so the confidence should be discounted.
    twrPct: 0.0392,
    apyPct: 0.199,
    sharpe: 1.18,
    maxDDPct: -0.026,
    netDeposits: 20000,
    dayChangePct: 0.0038
  },

  // ---- Draft bots ---------------------------------------------------
  // Unfinished bots saved mid-build. No capital deployed, not trading
  // — zeroed value/perf fields. Surface only under the "Drafts" filter
  // (or "All" when the user opts in). Sergey 2026-05-13.
  {
    id: "bot-draft-sector-rotation",
    name: "Sector Rotation (draft)",
    accountId: "acc-live-1",
    status: "PAUSED",
    isDraft: true,
    botType: "rebalancing",
    allocatedAmount: 0,
    currentValue: 0,
    performancePct: 0,
    lastTradeDate: "—",
    sparkline: [],
    tickers: ["XLK", "XLF", "XLE"],
    description: "Rotate monthly into the strongest of 11 SPDR sectors.",
    buyingPower: 0,
    allTimeGain: 0,
    allTimeGainPct: 0,
    createdAt: "2026-05-09",
    twrPct: 0,
    apyPct: 0,
    sharpe: 0,
    maxDDPct: 0,
    netDeposits: 0,
    dayChangePct: 0
  },
  {
    id: "bot-draft-vol-hedge",
    name: "Volatility Hedge (draft)",
    accountId: "acc-1",
    status: "PAUSED",
    isDraft: true,
    botType: "wizard",
    allocatedAmount: 0,
    currentValue: 0,
    performancePct: 0,
    lastTradeDate: "—",
    sparkline: [],
    tickers: ["SPY", "VIXY"],
    description: "Tilt into VIXY when the SPY 20d trend turns negative.",
    buyingPower: 0,
    allTimeGain: 0,
    allTimeGainPct: 0,
    createdAt: "2026-05-12",
    twrPct: 0,
    apyPct: 0,
    sharpe: 0,
    maxDDPct: 0,
    netDeposits: 0,
    dayChangePct: 0
  }
];

export const MARKETPLACE: MarketplaceTemplate[] = [
  {
    id: "tpl-four-pillars",
    name: "Four Pillars",
    creator: "WHB",
    description: "Equal-weight rotation between four uncorrelated asset classes, rebalanced monthly with a volatility filter.",
    assets: "Four Pillars Symbols List",
    ar: 503.7,
    winRate: 0,
    maxDrawdown: 11.4,
    listedDate: "2026-04-14",
    tags: ["ETF", "Long-only", "Rotation"],
    cumulativeReturn: 1842.5,
    sharpe: 2.31,
    calmar: 44.2,
    stdDev: 14.6,
    trailing1M: 3.1,
    trailing3M: 9.4,
    oosDate: "2026-01-21",
    recoveryTimeMonths: 4
  },
  {
    id: "tpl-golden-compass",
    name: "Golden Compass Rotation",
    creator: "WHB",
    description: "Sector rotation driven by a composite momentum + breadth signal.",
    assets: "Golden Compass Rotation Symbols List",
    ar: 140.8,
    winRate: 0,
    maxDrawdown: 57.8,
    listedDate: "2026-02-15",
    tags: ["ETF", "Sector", "Rotation"],
    cumulativeReturn: 612.4,
    sharpe: 1.18,
    calmar: 2.44,
    stdDev: 31.2,
    trailing1M: -2.4,
    trailing3M: 6.8,
    oosDate: "2025-11-30",
    recoveryTimeMonths: 14
  },
  {
    id: "tpl-stormwatch",
    name: "Stormwatch Protocol",
    creator: "WHB",
    description: "Defensive regime detector — flips to treasuries when SPY MA200 turns negative.",
    assets: "Defensive Rotation List",
    ar: 86.2,
    winRate: 0,
    maxDrawdown: 19.1,
    listedDate: "2026-02-12",
    tags: ["Defensive", "Regime", "ETF"],
    cumulativeReturn: 318.0,
    sharpe: 1.62,
    calmar: 4.51,
    stdDev: 12.1,
    trailing1M: 1.2,
    trailing3M: 3.0,
    oosDate: "2025-12-15",
    recoveryTimeMonths: 7
  },
  {
    id: "tpl-wizard",
    name: "Wizard Bot",
    creator: "WHB",
    description: "Experimental meta-strategy combining RSI, MACD, and volume profile signals.",
    assets: "TQQQ, QQQ, SQQQ",
    ar: 212.5,
    winRate: 0,
    maxDrawdown: 32.7,
    listedDate: "2026-01-22",
    tags: ["Leveraged", "Momentum"],
    botType: "wizard",
    cumulativeReturn: 904.1,
    sharpe: 1.05,
    calmar: 6.50,
    stdDev: 38.4,
    trailing1M: 5.6,
    trailing3M: -4.1,
    oosDate: "2026-01-10",
    recoveryTimeMonths: 9
  },
  {
    id: "tpl-holy-grail",
    name: "The Holy Grail",
    creator: "Community Member",
    description: "Risk-parity blend of equities, gold, and long bonds with a momentum overlay.",
    assets: "SPY, GLD, TLT, QQQ",
    ar: 121.8,
    winRate: 0,
    maxDrawdown: 47.0,
    listedDate: "2026-03-02",
    tags: ["ETF", "Long-only", "Defensive"],
    cumulativeReturn: 2000.9,
    sharpe: 1.44,
    calmar: 2.59,
    stdDev: 22.7,
    trailing1M: -1.1,
    trailing3M: 4.6,
    oosDate: "2025-10-04",
    recoveryTimeMonths: 11
  },
  {
    id: "tpl-rolling-momentum",
    name: "Rolling Momentum Shifter",
    creator: "Community Member",
    description: "Shifts weekly into the strongest of a 12-ETF universe on a 40-day relative-strength rank.",
    assets: "Sector ETF universe",
    ar: 96.3,
    winRate: 0,
    maxDrawdown: 28.4,
    listedDate: "2026-04-28",
    tags: ["ETF", "Rotation", "Momentum"],
    cumulativeReturn: 421.7,
    sharpe: 1.33,
    calmar: 3.39,
    stdDev: 19.8,
    trailing1M: -1.2,
    trailing3M: 7.3,
    oosDate: "2026-03-24",
    recoveryTimeMonths: 5
  },
  {
    id: "tpl-hedged-sector",
    name: "Hedged Sector Rotator",
    creator: "WHB",
    description: "Long the top-3 momentum sectors, hedged with an inverse-SPY sleeve when breadth deteriorates.",
    assets: "11 SPDR sectors + SH",
    ar: 73.5,
    winRate: 0,
    maxDrawdown: 16.2,
    listedDate: "2026-03-19",
    tags: ["Sector", "Defensive", "Rotation"],
    cumulativeReturn: 256.8,
    sharpe: 1.71,
    calmar: 4.54,
    stdDev: 13.4,
    trailing1M: 0.3,
    trailing3M: 2.1,
    oosDate: "2026-02-01",
    recoveryTimeMonths: 6
  },
  {
    id: "tpl-vol-harvest",
    name: "Volatility Harvest",
    creator: "Community Member",
    description: "Sells volatility in calm regimes, flips defensive when the VIX term structure inverts.",
    assets: "SVXY, VIXY, SPY, BIL",
    ar: 158.9,
    winRate: 0,
    maxDrawdown: 41.3,
    listedDate: "2025-12-08",
    tags: ["Leveraged", "Regime"],
    botType: "wizard",
    cumulativeReturn: 1107.2,
    sharpe: 0.97,
    calmar: 3.85,
    stdDev: 34.9,
    trailing1M: 4.2,
    trailing3M: -6.7,
    oosDate: "2025-09-18",
    recoveryTimeMonths: 18
  }
];

export const TESTING_SESSIONS: TestingSession[] = [
  { id: "ts-1", startedAt: "2026-02-25 13:46:31", botName: "Golden Compass Rotation", portfolio: "Golden Compass Rotation", notes: "5/01/25", return: 0.384, winRate: 0.58, trades: 47 },
  { id: "ts-2", startedAt: "2026-02-25 13:45:14", botName: "Stormwatch Protocol",     portfolio: "Stormwatch Protocol",     notes: "1/1/2026-2/26/2026", return: 0.042, winRate: 0.62, trades: 12 },
  { id: "ts-3", startedAt: "2026-02-12 19:31:21", botName: "Stormwatch Protocol",     portfolio: "Stormwatch Protocol",     notes: "10k 04/20/2023 - 02/11/2026", return: 0.221, winRate: 0.55, trades: 88 },
  { id: "ts-4", startedAt: "2026-01-22 23:10:32", botName: "Wizard bot",              portfolio: "RSI8",                    notes: "Default", return: 0.156, winRate: 0.59, trades: 34 },
  { id: "ts-5", startedAt: "2026-01-22 15:59:29", botName: "Wizard bot",              portfolio: "TQQQ",                    notes: "8%", return: 0.087, winRate: 0.51, trades: 21 },
  { id: "ts-6", startedAt: "2026-01-22 15:42:18", botName: "Wizard bot",              portfolio: "TQQQ",                    notes: "7%, 25k, 17% step", return: 0.065, winRate: 0.48, trades: 19 },
  { id: "ts-7", startedAt: "2026-01-22 14:43:37", botName: "Wizard bot",              portfolio: "TQQQ",                    notes: "23-2026 25k-100k", return: 0.112, winRate: 0.53, trades: 29 }
];

// ---- Bot report metrics helpers ------------------------------------------

/**
 * Per-bot derived fields that depend on the *current cohort* — i.e. the
 * subset of bots being rendered. Allocation % and contribution % make
 * sense only relative to a total, so we compute them at render time
 * against whichever bot list the advanced report is iterating.
 *
 * Age is purely derived from `createdAt` and a reference date ("now").
 * The reference date is injectable so a static report can be frozen at
 * a specific snapshot without the age ticking up on every reload.
 */
export interface BotDerivedMetrics {
  /** % of cohort total currentValue, decimal (e.g. 0.18 = 18%). */
  allocPct: number;
  /** % of cohort total dollarReturn, decimal — how much of the
   *  portfolio's gain is attributable to this bot. Negative if the
   *  bot is the only loser in a profitable cohort, etc. */
  contribPct: number;
  /** Days since `createdAt`. Integer days, no partial-day rounding. */
  ageDays: number;
}

/** Aggregate numbers across a bot cohort — used for the portfolio row
 *  at the top of the advanced report. */
export interface BotPortfolioSummary {
  totalValue: number;
  totalDollarReturn: number;
  totalNetDeposits: number;
  /** Blended TWR — weighted by netDeposits so larger bots dominate. */
  weightedTwrPct: number;
  /** Blended APY — same weighting scheme. */
  weightedApyPct: number;
  /** Blended Sharpe — netDeposits-weighted mean. Not a true pooled
   *  Sharpe (that would require return streams) but close enough for
   *  the report-view summary. */
  weightedSharpe: number;
  /** Worst single-bot maxDD — a more useful aggregate than a blended
   *  drawdown, since portfolio drawdowns are typically driven by the
   *  single worst drawdown at a given moment. */
  worstMaxDDPct: number;
  /** Simple arithmetic mean of bot dayChangePct, weighted by
   *  currentValue. */
  weightedDayChangePct: number;
  /** Number of bots summarised. */
  count: number;
}

/**
 * Compute `allocPct`, `contribPct`, `ageDays` per bot in the cohort.
 * The returned map is keyed by bot id; callers merge with the raw
 * Bot record as needed.
 *
 * `now` defaults to the app-wide mock "today" (2026-04-17) so ages
 * stay stable between renders. Override for test fixtures.
 */
export function deriveBotReportMetrics(
  bots: Bot[],
  now: Date = new Date("2026-04-17T00:00:00Z")
): Map<string, BotDerivedMetrics> {
  const totalValue = bots.reduce((s, b) => s + b.currentValue, 0);
  const totalReturn = bots.reduce((s, b) => s + b.allTimeGain, 0);

  const out = new Map<string, BotDerivedMetrics>();
  for (const b of bots) {
    // `createdAt` is yyyy-mm-dd in the mock dataset, but real-bot
    // adapters historically passed a full ISO timestamp (e.g.
    // `2025-06-13T17:29:51Z`). Slice the date portion before
    // appending — concatenating onto an already-complete ISO string
    // yields an invalid Date and `ageDays` becomes NaN, which
    // surfaces as "NaN years" in the detail drawer (Sergey 2026-04-26).
    const ymd = (b.createdAt ?? "").slice(0, 10);
    const created = ymd.length === 10 ? new Date(`${ymd}T00:00:00Z`) : new Date(NaN);
    const ageDays = Number.isFinite(created.getTime())
      ? Math.max(
          0,
          Math.floor((now.getTime() - created.getTime()) / 86_400_000)
        )
      : 0;
    // Guard against divide-by-zero when the cohort has zero aggregate
    // value / return — contribution collapses to 0 in that edge case.
    const allocPct = totalValue > 0 ? b.currentValue / totalValue : 0;
    const contribPct =
      totalReturn !== 0 ? b.allTimeGain / totalReturn : 0;
    out.set(b.id, { allocPct, contribPct, ageDays });
  }
  return out;
}

/**
 * Compute the portfolio-row summary for a bot cohort. Stable across
 * calls with the same input — pure aggregation.
 */
export function summarizeBotPortfolio(bots: Bot[]): BotPortfolioSummary {
  if (bots.length === 0) {
    return {
      totalValue: 0,
      totalDollarReturn: 0,
      totalNetDeposits: 0,
      weightedTwrPct: 0,
      weightedApyPct: 0,
      weightedSharpe: 0,
      worstMaxDDPct: 0,
      weightedDayChangePct: 0,
      count: 0
    };
  }
  const totalValue = bots.reduce((s, b) => s + b.currentValue, 0);
  const totalDollarReturn = bots.reduce((s, b) => s + b.allTimeGain, 0);
  const totalNetDeposits = bots.reduce((s, b) => s + b.netDeposits, 0);
  // Weight TWR/APY/Sharpe by netDeposits (not currentValue) so a bot
  // that grew large via performance doesn't over-dominate the blend.
  const depWeight = totalNetDeposits > 0 ? totalNetDeposits : 1;
  const weightedTwrPct =
    bots.reduce((s, b) => s + b.twrPct * b.netDeposits, 0) / depWeight;
  const weightedApyPct =
    bots.reduce((s, b) => s + b.apyPct * b.netDeposits, 0) / depWeight;
  const weightedSharpe =
    bots.reduce((s, b) => s + b.sharpe * b.netDeposits, 0) / depWeight;
  const worstMaxDDPct = bots.reduce(
    (min, b) => (b.maxDDPct < min ? b.maxDDPct : min),
    0
  );
  // Day change is weighted by currentValue — reflects "how much of
  // today's portfolio $ move came from which bot".
  const valWeight = totalValue > 0 ? totalValue : 1;
  const weightedDayChangePct =
    bots.reduce((s, b) => s + b.dayChangePct * b.currentValue, 0) / valWeight;
  return {
    totalValue,
    totalDollarReturn,
    totalNetDeposits,
    weightedTwrPct,
    weightedApyPct,
    weightedSharpe,
    worstMaxDDPct,
    weightedDayChangePct,
    count: bots.length
  };
}

export const STOCKS_LISTS: StocksList[] = [
  { id: "lst-1", name: "ETF Low Volatility",          tickers: ["AVUV","EEMV","EFAV","FDLO","GLD","IEF","SHY","USMV","VEA","VIG","VYM","XMLV","XSHD","MTUM"], creator: "WHB", updatedAt: "2026-04-01" },
  { id: "lst-2", name: "ETF Vanguard Sectors",        tickers: ["VAW","VCR","VDC","VDE","VFH","VHT","VIS","VGT","VPU","VOX","VNQ"], creator: "WHB", updatedAt: "2026-03-15" },
  { id: "lst-3", name: "Low Volatility ETFs Master",  tickers: ["AVUV","EEMV","EFAV","FDLO","GLD","IEF","SHY","USMV","VEA","VIG","VYM","XMLV","XSHD","MTUM"], creator: "You", updatedAt: "2026-02-10" },
  { id: "lst-4", name: "ETFs Low + Vanguard Sector",  tickers: ["AVUV","EEMV","EFAV","FDLO","GLD","IEF","SHY","USMV","VEA","VIG","VYM","XMLV","XSHD","MTUM","VAW","VCR","VDC","VDE","VFH","VHT","VIS","VGT","VPU","VOX","VNQ"], creator: "You", updatedAt: "2026-02-10" },
  { id: "lst-5", name: "Vanguard Sectors ETFs Master",tickers: ["VAW","VCR","VDC","VDE","VFH","VHT","VIS","VGT","VPU","VOX","VNQ"], creator: "WHB", updatedAt: "2026-03-15" },
  { id: "lst-6", name: "ETFs for VIX MidTerm Futures Strategy", tickers: ["PULS","SPY","SVXY","UVXY","VXZ"], creator: "WHB", updatedAt: "2026-01-08" }
];
